// These calls bypass supabase-js on purpose (binary downloads / streaming);
// the timeout policy must not bypass with them.
import { fetchWithTimeout } from "@/lib/supabaseFetch";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { formatDoorsRange, PRO_PRODUCTIONS_PER_YEAR } from "@produce/shared";
import { useProducerTimeFormatter } from "@/hooks/useTimeFormatter";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Calendar, MapPin, Clock, FileText, Edit, Download } from "lucide-react";
import AppSidebar from "@/components/AppSidebar";
import { useState, useEffect } from "react";
import type { BriefingStage, CateringMealConfig, Restrictions } from "@/types/domain";
import { parseStages, parseCateringEntries } from "@/types/domain";
import { useAuth } from "@/contexts/AuthContext";
import { reconstructFestivalDays } from "@/lib/doorsForDay";
import { stagesForDay } from "@/lib/briefingStages";
import { DayTabs } from "@/components/briefing/DayTabs";
import { CustomFieldsCard } from "@/components/briefing/CustomFieldsCard";
import { VideoExamplesCard } from "@/components/briefing/VideoExamplesCard";
import { FlightsCard } from "@/components/briefing/FlightsCard";
import { useFestivalDays } from "@/hooks/useFestivalDays";
import { invalidateBriefingCaches } from "@/lib/invalidateBriefingCaches";
import { format, parseISO } from "date-fns";

const ReviewPublishBriefing = () => {
  // Honours the producer's 12h/24h setting — this rendered a raw HH:MM cut.
  const formatTime = useProducerTimeFormatter();
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userId, session } = useAuth();
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  // DEFENSE IN DEPTH: explicit producer_id filter
  const { data: briefing, isLoading } = useQuery({
    queryKey: ["briefing", id],
    queryFn: async () => {
      if (!userId) return null;

      // Scope enforced by RLS (owner / co-producer / admin).
      const { data, error } = await supabase.from("briefings").select("*").eq("id", id!).single();

      if (error) throw error;
      return data;
    },
    enabled: !!id && !!userId,
  });

  // Per-day doors overrides for multi-day briefings.
  const { data: dayDoors = [] } = useQuery({
    queryKey: ["briefing_day_doors", id],
    queryFn: async () => {
      if (!id) return [];
      const { data, error } = await supabase
        .from("briefing_day_doors")
        .select("event_date, doors_open, doors_close")
        .eq("briefing_id", id);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!id,
  });

  // Fetch crew members for arrival times
  const { data: crewMembers } = useQuery({
    queryKey: ["crew-members", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("crew_members")
        .select("*")
        .eq("briefing_id", id!)
        .order("event_date", { ascending: true })
        .order("arrival_time", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  // Canonical per-day stages for the multi-day day selector.
  const { data: briefingStages = [] } = useQuery({
    queryKey: ["briefing_stages", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("briefing_stages")
        .select("*")
        .eq("briefing_id", id!)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!id,
  });

  // Day list + selected day for the multi-day Day 1 / Day 2 / Day 3
  // selectors on the stages + crew sections.
  const { festivalDates, isMultiDay, selectedDate, setSelectedDate } = useFestivalDays(
    briefing,
    dayDoors,
  );

  // Can this producer publish at all (active subscription)?
  const { data: publishCheck } = useQuery({
    queryKey: ["publish-check"],
    queryFn: async () => {
      if (!userId) return null;

      const { data, error } = await supabase.rpc("check_can_publish_production", {
        p_user_id: userId,
      });

      if (error) throw error;
      return data?.[0] || null;
    },
    enabled: !!userId,
  });

  const publishMutation = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not authenticated");

      // Check if user can publish
      if (publishCheck && !publishCheck.can_publish) {
        throw new Error(publishCheck.reason || "Cannot publish production");
      }

      // Update briefing to published
      const { data, error } = await supabase
        .from("briefings")
        .update({
          status: "published",
          published_at: new Date().toISOString(),
        })
        .eq("id", id!)
        .select()
        .single();

      if (error) throw error;

      return data;
    },
    onSuccess: async () => {
      if (id) await invalidateBriefingCaches(queryClient, id);
      queryClient.invalidateQueries({ queryKey: ["publish-check"] });
      toast({
        title: "Briefing Published",
        description: "Crew members will be notified.",
      });
      navigate(`/productions/${id}/crew`);
    },
    onError: (error: Error) => {
      toast({
        title: "Cannot Publish",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <AppSidebar />
        <main className="flex flex-1 items-center justify-center bg-muted/20 p-8">
          <p className="text-muted-foreground">Loading...</p>
        </main>
      </div>
    );
  }

  if (!briefing) {
    return (
      <div className="flex min-h-screen">
        <AppSidebar />
        <main className="flex flex-1 items-center justify-center bg-muted/20 p-8">
          <p className="text-muted-foreground">Briefing not found</p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <AppSidebar />

      <main className="flex-1 bg-muted/20 p-8">
        <div className="mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-8 flex items-start justify-between">
            <div>
              <h1 className="mb-2 text-3xl font-bold">
                {briefing.festival_name || "Untitled Briefing"}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {briefing.status === "published" && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      setIsGeneratingPDF(true);

                      const response = await fetchWithTimeout(
                        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-briefing-pdf`,
                        {
                          method: "POST",
                          headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${session?.access_token}`,
                          },
                          body: JSON.stringify({ briefingId: id }),
                        },
                      );

                      if (!response.ok) {
                        throw new Error("Failed to generate PDF");
                      }

                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `PROduce - ${briefing.festival_name || "Briefing"} - Briefing.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);

                      toast({
                        title: "Success",
                        description: "Briefing PDF downloaded successfully",
                      });
                    } catch (error) {
                      console.error(error);
                      toast({
                        title: "Error",
                        description: "Failed to download PDF",
                        variant: "destructive",
                      });
                    } finally {
                      setIsGeneratingPDF(false);
                    }
                  }}
                  disabled={isGeneratingPDF}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  {isGeneratingPDF ? "Generating PDF..." : "Download Briefing (PDF)"}
                </Button>
              )}
              <Badge
                variant={briefing.status === "published" ? "default" : "secondary"}
                className="px-4 py-2 text-lg"
              >
                {briefing.status === "published" ? "Published" : "Draft"}
              </Badge>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2">
            <Card className="bg-card p-6">
              <div className="mb-3 flex items-center gap-3">
                <Calendar className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Event dates</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {briefing.festival_date_start &&
                briefing.festival_date_end &&
                briefing.festival_date_start !== briefing.festival_date_end
                  ? `${new Date(briefing.festival_date_start).toLocaleDateString()} - ${new Date(briefing.festival_date_end).toLocaleDateString()}`
                  : briefing.festival_date_start
                    ? new Date(briefing.festival_date_start).toLocaleDateString()
                    : "Not set"}
              </p>
            </Card>

            <Card className="bg-card p-6">
              <div className="mb-3 flex items-center gap-3">
                <Clock className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Doors</h3>
              </div>
              {(() => {
                const days =
                  briefing.festival_date_start && briefing.festival_date_end
                    ? reconstructFestivalDays({
                        startDate: briefing.festival_date_start,
                        endDate: briefing.festival_date_end,
                        perDayDoors: dayDoors,
                        fallback: {
                          doors_open: briefing.doors_open ?? null,
                          doors_close: briefing.doors_close ?? null,
                        },
                      })
                    : [];
                const anySet = days.some((d) => d.startTime || d.endTime);
                if (days.length === 0 || !anySet)
                  return <p className="text-sm text-muted-foreground">Not set</p>;
                if (days.length === 1)
                  return (
                    <p className="text-sm text-muted-foreground">
                      {formatDoorsRange(days[0], formatTime)}
                    </p>
                  );
                return (
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    {days.map((d) => (
                      <li key={d.date}>
                        {format(parseISO(d.date), "EEE d MMM")}:{" "}
                        {d.startTime || d.endTime ? formatDoorsRange(d, formatTime) : "Not set"}
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </Card>

            <Card className="bg-card p-6">
              <div className="mb-3 flex items-center gap-3">
                <MapPin className="h-5 w-5 text-primary" />
                <h3 className="font-semibold">Navigation address</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {briefing.navigation_address || "Not set"}
              </p>
            </Card>
          </div>

          {/* General Briefing Information */}
          <Card className="mb-6 bg-card p-8">
            <h2 className="mb-6 text-2xl font-bold">General briefing information</h2>

            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Location</h3>
                  <p className="text-foreground">{briefing.location || "Not set"}</p>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                    Navigation address
                  </h3>
                  <p className="text-foreground">{briefing.navigation_address || "Not set"}</p>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                    Hotel address
                  </h3>
                  <p className="text-foreground">{briefing.hotel_address || "Not set"}</p>
                </div>
              </div>

              {(briefingStages.length > 0 ||
                (briefing.stages && parseStages(briefing.stages).length > 0)) &&
                (() => {
                  // Prefer the canonical per-day briefing_stages rows; for
                  // a multi-day festival show one day at a time behind the
                  // Day buttons (no more duplicated stages). Fall back to
                  // the legacy JSONB list for old single-day briefings.
                  const labels =
                    briefingStages.length > 0
                      ? stagesForDay(briefingStages, selectedDate).map((s) => s.label)
                      : parseStages(briefing.stages).map((s: BriefingStage) => s.label || s.name);
                  return (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Stages</h3>
                      <DayTabs
                        dates={festivalDates}
                        selectedDate={selectedDate}
                        onSelect={setSelectedDate}
                        dayNames={briefing.day_names}
                      />
                      <div className="flex flex-wrap gap-2">
                        {labels.length > 0 ? (
                          labels.map((label, idx) => (
                            <Badge key={idx} variant="outline">
                              {label}
                            </Badge>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">No stages for this day.</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

              {briefing.crew_checkin && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                    Crew check-in information
                  </h3>
                  <p className="text-foreground">{briefing.crew_checkin}</p>
                </div>
              )}

              {briefing.crew_dresscode && (
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                    Crew dresscode
                  </h3>
                  <p className="text-foreground">{briefing.crew_dresscode}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Catering */}
          {briefing.catering && Object.keys(briefing.catering as object).length > 0 && (
            <Card className="mb-6 bg-card p-8">
              <h2 className="mb-6 text-2xl font-bold">Catering</h2>
              <div className="grid grid-cols-2 gap-6">
                {parseCateringEntries(briefing.catering).map(
                  ([key, value]: [string, CateringMealConfig]) =>
                    value &&
                    value.start && (
                      <div key={key}>
                        <h3 className="mb-2 text-sm font-semibold capitalize text-muted-foreground">
                          {key}
                        </h3>
                        <p className="text-foreground">
                          {value.start} - {value.end}
                        </p>
                      </div>
                    ),
                )}
              </div>
            </Card>
          )}

          {/* Technical Settings */}
          {(briefing.camera_settings || briefing.audio_settings || briefing.extra_settings) && (
            <Card className="mb-6 bg-card p-8">
              <h2 className="mb-6 text-2xl font-bold">Technical settings</h2>
              <div className="space-y-6">
                {briefing.camera_settings &&
                  Object.keys(briefing.camera_settings as object).length > 0 && (
                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                        Camera settings
                      </h3>
                      <div className="space-y-2">
                        {Object.entries(briefing.camera_settings as object)
                          .filter(([key]) => key !== "text")
                          .map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <span className="capitalize text-muted-foreground">
                                {key.replace(/_/g, " ")}:
                              </span>
                              <span className="text-foreground">{String(value)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                {briefing.audio_settings && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Audio settings
                    </h3>
                    <p className="text-foreground">{briefing.audio_settings}</p>
                  </div>
                )}

                {briefing.extra_settings && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Extra settings
                    </h3>
                    <p className="text-foreground">{briefing.extra_settings}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Restrictions & Instructions */}
          {(briefing.restrictions || briefing.general_instructions || briefing.others) && (
            <Card className="mb-6 bg-card p-8">
              <h2 className="mb-6 text-2xl font-bold">Restrictions & instructions</h2>
              <div className="space-y-6">
                {briefing.restrictions && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Restrictions
                    </h3>
                    <p className="whitespace-pre-wrap text-foreground">
                      {typeof briefing.restrictions === "object" &&
                      (briefing.restrictions as Restrictions).text
                        ? (briefing.restrictions as Restrictions).text
                        : typeof briefing.restrictions === "string"
                          ? briefing.restrictions
                          : JSON.stringify(briefing.restrictions, null, 2)}
                    </p>
                  </div>
                )}

                {briefing.general_instructions && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      General instructions
                    </h3>
                    <p className="whitespace-pre-wrap text-foreground">
                      {briefing.general_instructions}
                    </p>
                  </div>
                )}

                {briefing.others && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                      Other notes
                    </h3>
                    <p className="whitespace-pre-wrap text-foreground">{briefing.others}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Flights, grouped per crew member. Producer-only surface:
              crew read their own flights in the app. */}
          <FlightsCard briefingId={briefing.id} enabled={!!briefing.flights_enabled} />

          <VideoExamplesCard videoExamples={briefing.video_examples} />

          {/* Producer-defined extra fields */}
          <CustomFieldsCard customFields={briefing.custom_fields} />

          {/* Crew Arrival Times */}
          {crewMembers && crewMembers.length > 0 && (
            <Card className="mb-6 bg-card p-8">
              <h2 className="mb-6 text-2xl font-bold">Crew arrival times</h2>
              <DayTabs
                dates={festivalDates}
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
                dayNames={briefing.day_names}
              />
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                        Function
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                        Arrival time
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isMultiDay
                      ? crewMembers.filter((m) => m.event_date === selectedDate)
                      : crewMembers
                    ).map((member) => (
                      <tr key={member.id} className="border-b border-border/50">
                        <td className="px-4 py-3 text-foreground">{member.name}</td>
                        <td className="px-4 py-3 text-foreground">{member.function}</td>
                        <td className="px-4 py-3 text-foreground">
                          {new Date(member.event_date).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-foreground">
                          {member.arrival_time ? formatTime(member.arrival_time) : "Not set"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {/* PRO: the yearly limit, so publishing never comes as a surprise. */}
          {briefing.status !== "published" && publishCheck?.plan_tier === "pro" && (
            <Card className="mb-6 border-primary/50 bg-card p-6">
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-5 w-5 text-primary" />
                <div>
                  <h3 className="mb-1 font-semibold">Counts toward your yearly limit</h3>
                  <p className="text-sm text-muted-foreground">
                    Your PRO plan includes {PRO_PRODUCTIONS_PER_YEAR} published productions per
                    calendar year. Publishing this production uses one of them.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <Link to={`/briefings/${id}/edit`}>
              <Button variant="outline">
                <Edit className="mr-2 h-4 w-4" />
                Edit Briefing
              </Button>
            </Link>
            {briefing.status !== "published" && (
              <Button
                onClick={() => publishMutation.mutate()}
                disabled={
                  publishMutation.isPending || !!(publishCheck && !publishCheck.can_publish)
                }
              >
                {publishMutation.isPending ? "Publishing..." : "Confirm & Publish"}
              </Button>
            )}
            <Link to="/dashboard">
              <Button>Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ReviewPublishBriefing;
